import {
    type RefObject,
    useCallback,
    useEffect,
    useRef,
    useState,
} from 'react';
import useSWR, { useSWRConfig } from 'swr';

import { getApiClient } from '@apps/web/lib/api_client';
import { type Mode } from '@libraries/typescript/api_client';

export const ATTACHMENT_URL_TEMPLATE =
    '/organizations/{organization_id}/phoebe_agent_runs/{phoebe_agent_run_id}/attachments/{attachment_id}' as const;

const ATTACHMENT_REQUEST_TIMEOUT_MS = 30_000;
const MAX_PREVIEW_CONCURRENCY = 4;

type PreviewTask<T> = {
    readonly run: () => Promise<T>;
    readonly signal: AbortSignal;
    readonly resolve: (value: T) => void;
    readonly reject: (error: unknown) => void;
};

const previewQueue: Array<PreviewTask<unknown>> = [];
let activePreviewRequests = 0;

function rejectAbortedPreview(): Error {
    const error = new Error('The operation was aborted');
    error.name = 'AbortError';
    return error;
}

function pumpPreviewQueue(): void {
    while (
        activePreviewRequests < MAX_PREVIEW_CONCURRENCY &&
        previewQueue.length > 0
    ) {
        const task = previewQueue.shift();
        if (!task) {
            return;
        }
        if (task.signal.aborted) {
            task.reject(rejectAbortedPreview());
            continue;
        }
        activePreviewRequests += 1;
        void task
            .run()
            .then(task.resolve, task.reject)
            .finally(() => {
                activePreviewRequests -= 1;
                pumpPreviewQueue();
            });
    }
}

function enqueuePreviewRequest<T>(
    run: () => Promise<T>,
    signal: AbortSignal,
): Promise<T> {
    return new Promise<T>((resolve, reject) => {
        previewQueue.push({
            run,
            signal,
            resolve: resolve as (value: unknown) => void,
            reject,
        });
        pumpPreviewQueue();
    });
}

export async function fetchAttachmentBlob({
    organizationId,
    runId,
    attachmentId,
    download,
    mode,
    signal,
}: {
    readonly organizationId: string;
    readonly runId: string;
    readonly attachmentId: string;
    readonly download: boolean;
    readonly mode: Mode;
    readonly signal: AbortSignal;
}): Promise<Blob> {
    // Use the generated binary response path so normal client timeout,
    // retries, credentials, and the effective X-Mode all stay in one place.
    return getApiClient().get({
        url: ATTACHMENT_URL_TEMPLATE,
        pathParams: {
            organization_id: organizationId,
            phoebe_agent_run_id: runId,
            attachment_id: attachmentId,
        },
        queryParams: download ? { download: true } : {},
        mode,
        reason: 'v3-chat-attachment',
        signal,
        timeoutMs: ATTACHMENT_REQUEST_TIMEOUT_MS,
    });
}

const MAX_INLINE_MARKDOWN_BYTES = 512 * 1024;
const MARKDOWN_PREVIEW_TRUNCATED =
    '_Preview truncated — download the full file to see the rest._';

export async function fetchAttachmentMarkdown(args: {
    readonly organizationId: string;
    readonly runId: string;
    readonly attachmentId: string;
    readonly mode: Mode;
    readonly sizeBytes: number;
    readonly signal: AbortSignal;
}): Promise<string> {
    if (args.sizeBytes > MAX_INLINE_MARKDOWN_BYTES) {
        return MARKDOWN_PREVIEW_TRUNCATED;
    }
    const blob = await fetchAttachmentBlob({
        ...args,
        download: false,
    });
    return blob.text();
}

export function useAttachmentSWR<T>(
    key: ReadonlyArray<unknown> | null,
    request: (signal: AbortSignal) => Promise<T>,
) {
    const { mutate } = useSWRConfig();
    const keyRef = useRef(key);
    keyRef.current = key;
    const controllerRef = useRef<AbortController | null>(null);
    const fetcher = useCallback(async () => {
        controllerRef.current?.abort();
        const controller = new AbortController();
        controllerRef.current = controller;
        try {
            return await enqueuePreviewRequest(
                () => request(controller.signal),
                controller.signal,
            );
        } finally {
            if (controllerRef.current === controller) {
                controllerRef.current = null;
            }
        }
    }, [request]);
    useEffect(() => {
        return () => {
            controllerRef.current?.abort();
            controllerRef.current = null;
            if (keyRef.current !== null) {
                try {
                    void mutate(keyRef.current, undefined, {
                        revalidate: false,
                    }).catch(() => undefined);
                } catch {
                    // The provider can unmount before this child cleanup.
                }
            }
        };
    }, [mutate]);
    return useSWR<T>(key, fetcher, {
        revalidateOnFocus: false,
        shouldRetryOnError: false,
    });
}

export function useAttachmentPreviewNearViewport(): {
    readonly ref: RefObject<HTMLDivElement | null>;
    readonly isNearViewport: boolean;
} {
    const ref = useRef<HTMLDivElement | null>(null);
    const [isNearViewport, setIsNearViewport] = useState(false);
    useEffect(() => {
        if (typeof IntersectionObserver === 'undefined') {
            setIsNearViewport(true);
            return;
        }
        const element = ref.current;
        if (!element) {
            return;
        }
        const observer = new IntersectionObserver(
            ([entry]) => setIsNearViewport(entry?.isIntersecting ?? false),
            { rootMargin: '400px 0px' },
        );
        observer.observe(element);
        return () => observer.disconnect();
    }, []);
    return { ref, isNearViewport };
}
